import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ViewIds } from '@ardoq/api-types';
import { registerViewPane } from 'tabview/assistantShared/registerViewPane';
import { colors } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  NETWORK_CAMERA,
  Network3DView as Network3DCanvas,
  resolveCssColor,
  toRgb01,
  toSurfaceAdaptiveTheme,
  type NetworkTheme,
  type NetworkMarkLocator,
  type NetworkCameraCommands,
  type NetworkContextTarget,
} from '@ardoq/xyz-charts';
import styled from 'styled-components';
import { VisualizationContainer } from 'tabview/ViewContainer';
import { dispatchAction } from '@ardoq/rxbeach';
import * as canvasHitTestRegistry from 'tabview/canvasRendering/canvasHitTestRegistry';
import type { Network3DViewProperties } from './types';
import {
  network3DMatchCountChanged,
  network3DSearchCleared,
} from './network3dSearch$';
import { ViewLegend } from '@ardoq/view-legend';
import { ViewLegendContainer } from '@ardoq/graph';
import { noop } from 'lodash';
import { Network3DEmptyState } from './Network3DEmptyState';
import type { Network3DViewModel } from './viewModel$/network3dViewModel$';

/** See `Surface3DView` for why `min-height: 0` is what keeps the canvas from collapsing. */
const Network3DLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const CanvasArea = styled(VisualizationContainer)`
  overflow: hidden;
  min-height: 0;
`;

/**
 * The tokens the network chrome is built from.
 *
 * Exported so a test can assert the view reads the theme rather than a hardcoded palette - the
 * failure this guards is a view that looks right in light mode and unreadable in dark, which no
 * type check catches.
 */
export const NETWORK_3D_COLOR_TOKENS = {
  surface: colors.surfaceDefault,
  ink: colors.textDefault,
  subtleInk: colors.textSubtle,
  border: colors.borderSubtle,
} as const;

/**
 * Builds the package's theme from the app's own tokens.
 *
 * The package ships `DARK_NETWORK_THEME` and `LIGHT_NETWORK_THEME` for its stories, and the app
 * uses neither: a story picks a surface, an app is *told* one. Resolving the live tokens is what
 * makes the view follow the theme the user is actually in, and it is also what keeps the fog
 * honest - fog washes distant geometry toward the surface color, so a theme whose fog came from
 * the wrong surface would make far nodes glow rather than recede.
 *
 * Mount-time only, with the same caveat `Surface3DView` documents: dark mode is a class on
 * `<body>` with nothing observable behind it, so toggling the theme with the view open leaves the
 * canvas on the old palette until it remounts.
 */
const useNetworkTheme = (element: HTMLElement | null): NetworkTheme =>
  useMemo(() => {
    const surface = resolveCssColor(
      element,
      NETWORK_3D_COLOR_TOKENS.surface,
      '#ffffff'
    );
    const ink = resolveCssColor(
      element,
      NETWORK_3D_COLOR_TOKENS.ink,
      '#040506'
    );
    const subtleInk = resolveCssColor(
      element,
      NETWORK_3D_COLOR_TOKENS.subtleInk,
      '#6B6B66'
    );
    const border = resolveCssColor(
      element,
      NETWORK_3D_COLOR_TOKENS.border,
      '#DADADA'
    );
    const surfaceChannels = toRgb01(surface);

    return {
      surface: surfaceChannels,
      /* Derived from the resolved surface rather than picked, because that resolution is the only
         thing here that knows whether the user is in dark mode. */
      ...toSurfaceAdaptiveTheme(surfaceChannels),
      edgeInk: toRgb01(subtleInk),
      guideInk: toRgb01(border),
      lighting: {
        ambient: 0.52,
        shininess: 20,
        lights: [
          {
            direction: [-0.15, -0.1, 0.98],
            space: 'world',
            color: [0.55, 0.72, 1],
            diffuse: 0.12,
            specular: 0.02,
          },
          {
            direction: [-0.25, 0.3, 0.92],
            space: 'view',
            color: [1, 1, 1],
            diffuse: 0.34,
            specular: 0.22,
          },
        ],
        /* Toward whatever the surface is, which is the whole point of resolving it. The strength
           comes from the same derivation as the blend, because it depends on which end of the
           range the surface sits at - a light surface needs less of it than a dark one. */
        fog: {
          color: surfaceChannels,
          strength: toSurfaceAdaptiveTheme(surfaceChannels).fogStrength,
        },
      },
      css: {
        surface,
        text: ink,
        mutedText: subtleInk,
        border,
      },
    } satisfies NetworkTheme;
  }, [element]);

/**
 * The canvas's accessible name, composed from two separately-pluralised counts.
 *
 * Two counts that vary independently cannot share one `_one`/`_other` pair - "1 component and 4
 * references" needs the singular of one and the plural of the other in the same sentence - so each
 * phrase is resolved on its own and the frame interpolates the results. That also lets a language
 * whose noun phrases decline differently translate each part properly instead of being handed a
 * template with two numbers in it.
 */
const toAriaLabel = (
  i18nLocale: I18nLocale,
  componentCount: number,
  referenceCount: number,
  collapsedGroupCount: number
): string => {
  const components = t(
    i18nLocale,
    componentCount === 1
      ? 'views:network3d.ariaLabel.components_one'
      : 'views:network3d.ariaLabel.components_other',
    { count: componentCount },
    componentCount === 1 ? '1 component' : '{{count}} components'
  );
  const references = t(
    i18nLocale,
    referenceCount === 1
      ? 'views:network3d.ariaLabel.references_one'
      : 'views:network3d.ariaLabel.references_other',
    { count: referenceCount },
    referenceCount === 1 ? '1 reference' : '{{count}} references'
  );

  if (collapsedGroupCount === 0) {
    return t(
      i18nLocale,
      'views:network3d.ariaLabel.summary',
      { components, references },
      '3D network of {{components}} and {{references}}. Drag to rotate, scroll to zoom.'
    );
  }

  /* A third phrase resolved on its own, for the reason the other two are: three counts that vary
     independently cannot share one plural pair. */
  const collapsedGroups = t(
    i18nLocale,
    collapsedGroupCount === 1
      ? 'views:network3d.ariaLabel.collapsedGroups_one'
      : 'views:network3d.ariaLabel.collapsedGroups_other',
    { count: collapsedGroupCount },
    collapsedGroupCount === 1
      ? '1 group collapsed'
      : '{{count}} groups collapsed'
  );

  /**
   * Its own frame rather than an interpolation left empty.
   *
   * A sentence with a slot for a clause that is usually absent is a sentence that reads as
   * punctuation in the common case - "3D network of 28 components and 30 references, ." - and no
   * translator can word the slot without seeing both forms. Two frames also lets a language put
   * the clause where its own grammar wants it.
   */
  return t(
    i18nLocale,
    'views:network3d.ariaLabel.summaryWithCollapsedGroups',
    { components, references, collapsedGroups },
    '3D network of {{components}} and {{references}}, {{collapsedGroups}}. Drag to rotate, scroll to zoom.'
  );
};

/* The settings arrive beside the derived data rather than inside it - see `ContourViewProps`.
   Everything else here is `toNetwork3DPresentation`'s: derived in the view model, because none of
   it needs a browser. */
export type Network3DViewProps = Network3DViewModel;

export const Network3DView = ({
  graph,
  numericFields,
  viewSettings,
  isViewEmpty,
  emptyReason,
  droppedReferenceCount,
  viewInstanceId,
  i18nLocale,
  drawnSettings,
  legendRows,
  viewLegendLabels,
  labels,
  droppedCopy,
  exportCaptions,
  foldedBehind,
  onNodeActivate,
  onContextMenu,
  hasGroups,
  isGrouped,
  componentCount,
  collapsedGroupCount,
  searchQuery,
  toComponentId,
}: Network3DViewProps) => {
  /* State behind a callback ref rather than a `useRef`, and that is the difference between the
     colors resolving and not - see `Points3DView` for the full account. */
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const theme = useNetworkTheme(container);

  /* Optimistic and corrected by the device resolving, exactly as `Surface3DView` does it: starting
     at false would disable the export menu for the moment before the adapter answers. */
  const [isDrawable, setIsDrawable] = useState(true);

  const reportMatchCount = useCallback(
    (matchCount: number) =>
      dispatchAction(network3DMatchCountChanged(matchCount)),
    []
  );

  /* The question stops being asked when the view goes away. Without this a reader who searched,
     switched view and came back would find the field holding yesterday's text over a picture that
     had already been filtered by it. */
  useEffect(() => () => dispatchAction(network3DSearchCleared()), []);

  /**
   * Registers the view as something a tether can be anchored to.
   *
   * A DOM view is found by its element; this one has to answer for itself, which is what the
   * package's locator is for. Held in state rather than a ref so the registration re-runs when the
   * canvas comes and goes - a registry entry pointing at a canvas that has gone would locate the
   * source of a reference being drawn to nowhere.
   *
   * `hitTest` answers in component ids because that is what the registry is read for, and a
   * grouping can draw one component as several marks - `toComponentId` is the only thing that
   * knows which component a mark stands for. `locate` answers by mark, which is what the handle
   * carries, so a tether follows the mark it was dropped on rather than some other mark of the
   * same component.
   */
  const [markLocator, setMarkLocator] = useState<NetworkMarkLocator | null>(
    null
  );

  useEffect(() => {
    if (markLocator === null) return;

    canvasHitTestRegistry.register(viewInstanceId, {
      hitTest: event => {
        const nodeId = markLocator.at(event);
        return nodeId === null ? null : toComponentId(nodeId);
      },
      locate: markLocator.locate,
    });

    /* Braced: `unregister` answers whether it removed anything, and a cleanup that returns
       something is read as a second cleanup. */
    return () => {
      canvasHitTestRegistry.unregister(viewInstanceId);
    };
  }, [markLocator, viewInstanceId, toComponentId]);

  const createExportCanvasRef = useRef<(() => OffscreenCanvas | null) | null>(
    null
  );
  const onExportCanvasAvailable = useCallback(
    (createExportCanvas: (() => OffscreenCanvas | null) | null) => {
      createExportCanvasRef.current = createExportCanvas;
    },
    []
  );

  /**
   * Zoom-to-fit, in the header's `optimizeLayout` slot.
   *
   * The slot rather than one of its own, which is the shape the header already has: a view with no
   * relayout to optimise puts its fit there, as Assistant Blocks and the Flexible Block Diagram
   * do. A dedicated fit control was built and closed as contrary to that design.
   *
   * Through a ref so the registration below does not re-run whenever the view republishes it. The
   * registration's own identity is what the header reads, and re-registering on every publish
   * would churn the whole view context for a callback that has not changed meaning.
   */
  const cameraRef = useRef<NetworkCameraCommands | null>(null);
  const onCameraCommandsAvailable = useCallback(
    (commands: NetworkCameraCommands | null) => {
      cameraRef.current = commands;
    },
    []
  );

  /**
   * Answers the one question the menu cannot: whether the clicked group has a region to fly into.
   *
   * Asked here because the camera is here. `canFrameGroup` and `frameGroup` are the same lookup on
   * the package's side, so an entry offered is an entry that does something.
   */
  const onCanvasContextMenu = useCallback(
    (
      target: NetworkContextTarget,
      at: { clientX: number; clientY: number }
    ) => {
      const camera = cameraRef.current;
      const nodeId = target.kind === 'node' ? target.nodeId : null;
      onContextMenu(target, at, {
        zoomToGroup:
          camera !== null && nodeId !== null && camera.canFrameGroup(nodeId)
            ? () => camera.frameGroup(nodeId)
            : null,
      });
    },
    [onContextMenu]
  );

  /* `registerViewPane` rather than `updateViewProperties` alone, for the export hook and for the
     view context - see `Points3DView` for why publishing properties without one inherits the
     previous view's actions. */
  useEffect(() => {
    registerViewPane({
      viewId: ViewIds.NETWORK_3D,
      viewProperties: {
        viewSettings,
        numericFields,
        i18nLocale,
        isEmptyView: isViewEmpty,
        isDrawable,
        hasGroups,
        isGrouped,
        viewInstanceId,
      } satisfies Network3DViewProperties,
      isLegendActive: viewSettings.isLegendActive,
      getContainer: () => container,
      createExportCanvas: () => createExportCanvasRef.current?.() ?? null,
      optimizeLayout: () => cameraRef.current?.fit(),
      viewInstanceId,
    });
  }, [
    viewSettings,
    numericFields,
    i18nLocale,
    isViewEmpty,
    isDrawable,
    hasGroups,
    isGrouped,
    viewInstanceId,
    container,
  ]);

  /* Rendered by both branches at the same position under the same layout, so the region is in the
     accessibility tree before it has anything to say - see `Points3DView` for why a live region
     that arrives with its first message announces nothing. */
  const droppedNotice = (
    <FlexBox
      role="status"
      aria-live="polite"
      padding={droppedReferenceCount > 0 ? 'small' : 'none'}
      paddingBottom="none"
    >
      {droppedReferenceCount > 0 && !isViewEmpty && (
        <Text color="textSubtle" variant="caption">
          {droppedCopy}
        </Text>
      )}
    </FlexBox>
  );

  if (isViewEmpty) {
    return (
      <Network3DLayout>
        {droppedNotice}
        <Network3DEmptyState
          emptyReason={emptyReason}
          i18nLocale={i18nLocale}
        />
      </Network3DLayout>
    );
  }

  return (
    <Network3DLayout>
      {droppedNotice}
      {/* The box a tether is clipped to, and how `sourceAnchor` finds this view at all. */}
      <CanvasArea ref={setContainer} data-canvas-hit-test={viewInstanceId}>
        {/* Ardoq's own legend rather than the package's strip along the canvas. The strip is the
            chart package's key for its stories, which have no app around them; in the app the
            legend is a panel the reader toggles and expects in the same place as every other
            view's. Both read the same colors, so what they say agrees. */}
        <ViewLegendContainer visible={viewSettings.isLegendActive}>
          <ViewLegend
            componentTypes={[]}
            referenceTypes={[]}
            additionalRows={legendRows}
            activeDiffMode={null}
            itemClickTrackingFunction={noop}
            labels={viewLegendLabels}
          />
        </ViewLegendContainer>
        <Network3DCanvas
          graph={graph}
          settings={drawnSettings}
          theme={theme}
          camera={NETWORK_CAMERA}
          /* Fixed rather than derived from the data: the layouts are deterministic in the seed, so
             a changing seed would reshuffle a settled graph on every unrelated re-render. A reseed
             is a user action the settings bar could offer later, not something the view decides. */
          seed={7}
          ariaLabel={toAriaLabel(
            i18nLocale,
            /* Components, not nodes, and resolved in the view model - see `componentCount`. It
               was `graph.nodes.filter(weight !== 0).length` here, on the premise that a
               weightless node is how a group says it is a region rather than a mark. Collapsing
               broke that premise: a closed group is a mark. */
            componentCount,
            graph.edges.length,
            collapsedGroupCount
          )}
          labels={labels}
          foldedBehind={foldedBehind}
          onNodeActivate={onNodeActivate}
          onContextMenu={onCanvasContextMenu}
          exportCaptions={exportCaptions}
          query={searchQuery}
          onMatchCountChange={reportMatchCount}
          onExportCanvasAvailable={onExportCanvasAvailable}
          onCameraCommandsAvailable={onCameraCommandsAvailable}
          onMarkLocatorAvailable={setMarkLocator}
          onDrawableChange={setIsDrawable}
        />
      </CanvasArea>
    </Network3DLayout>
  );
};
